'use client';

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

const CONTROL_CLASSES =
  'w-full rounded-[var(--field-radius,0.75rem)] border border-line-strong bg-ink/70 px-[var(--field-px,1rem)] py-[var(--field-py,0.75rem)] text-[length:var(--field-font,1rem)] text-content placeholder:text-content-subtle transition focus:border-brand focus:bg-ink focus:outline-none disabled:opacity-60';

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/**
 * Wires up label/hint/error ids for any control so every field is accessible
 * by construction rather than by remembering to add aria attributes.
 */
function FieldShell({ label, hint, error, required, className, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-[var(--field-gap,0.5rem)]', className)}>
      <label htmlFor={id} className="text-[color:var(--field-label,var(--color-content-muted))] text-sm font-medium">
        {label}
        {required ? <span className="text-brand-bright ml-1">*</span> : null}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {hint && !error ? (
        <p id={hintId} className="text-content-subtle text-xs">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-danger text-xs font-medium">
          {error}
        </p>
      ) : null}
    </div>
  );
}

type BaseFieldProps = Omit<FieldShellProps, 'children'>;

export type TextFieldProps = BaseFieldProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'>;

export function TextField({ label, hint, error, required, className, ...inputProps }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          className={cn(CONTROL_CLASSES, invalid && 'border-danger focus:border-danger')}
          {...inputProps}
        />
      )}
    </FieldShell>
  );
}

export type TextAreaFieldProps = BaseFieldProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className'>;

export function TextAreaField({ label, hint, error, required, className, rows = 5, ...props }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          className={cn(CONTROL_CLASSES, 'resize-y', invalid && 'border-danger focus:border-danger')}
          {...props}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectFieldProps = BaseFieldProps & {
  options: SelectOption[];
  placeholder?: string;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className'>;

export function SelectField({
  label,
  hint,
  error,
  required,
  className,
  options,
  placeholder,
  ...props
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          className={cn(CONTROL_CLASSES, 'appearance-none pr-10', invalid && 'border-danger focus:border-danger')}
          {...props}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
  name,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  name?: string;
}) {
  return (
    <label className="text-content-muted flex cursor-pointer items-start gap-3 text-sm">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="accent-brand mt-0.5 h-5 w-5 shrink-0 rounded"
      />
      <span>{label}</span>
    </label>
  );
}
